import { useIntl } from '@capsicum/common';
import { Skeleton, SkeletonItem } from '@capsicum/common-web';
import { ChilizIcon, DollarIcon } from '@capsicum/common-web/icons';
import { type EnhancedWalletCurrency } from '@capsicum/features/wallet/shared';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { S } from './WalletBalance.styled';
import { WalletTooltips, WalletTooltipType } from './WalletTooltips';

export type WalletBalanceProps = {
	altCurrency?: EnhancedWalletCurrency;
	defaultCurrency?: EnhancedWalletCurrency;
	isUsaRegulation: boolean;
};

export const WalletBalance = ({ altCurrency, defaultCurrency, isUsaRegulation }: WalletBalanceProps) => {
	const { colors } = useTheme();
	const { $t } = useIntl();

	const isDefaultCurrency = defaultCurrency && Object.keys(defaultCurrency).length;

	const currencyIcon = useMemo(() => {
		return isUsaRegulation ? (
			<DollarIcon color={colors.icons.main.inverse} />
		) : (
			<ChilizIcon color={colors.icons.main.inverse} />
		);
	}, [isUsaRegulation, colors]);

	const balanceTexts = {
		availableBalance: $t('wallet.walletBalance.currencyInfo', {
			code: (isDefaultCurrency && defaultCurrency?.shortCode) ?? '',
			name: (isDefaultCurrency && defaultCurrency?.label) ?? ''
		}),
		lockedBalance: $t('wallet.walletBalance.lockedLabel')
	};

	const enoughLockedBalance = !!defaultCurrency?.rawLockedBalance;

	const balanceInfoCurrency = isDefaultCurrency ? `${defaultCurrency?.formattedBalance}` : '';

	const isBalanceInfoLocked = Boolean(isDefaultCurrency && defaultCurrency?.lockedBalance && enoughLockedBalance);

	const balanceInfoLocked = isDefaultCurrency
		? `${defaultCurrency?.formattedLockedBalance} ${balanceTexts.lockedBalance}`
		: '';

	return (
		<S.Container>
			<S.ContainerWrapper>
				<S.CurrencyIconWrapper>{currencyIcon}</S.CurrencyIconWrapper>
				<S.InfoContainer>
					<S.InfoTitle>
						{defaultCurrency ? (
							<>
								<span>{balanceTexts.availableBalance}</span>
								<WalletTooltips type={WalletTooltipType.BALANCE} />
							</>
						) : (
							<Skeleton>
								<SkeletonItem width="26ch" />
							</Skeleton>
						)}
					</S.InfoTitle>
					<S.InfoCurrency>
						{defaultCurrency ? (
							balanceInfoCurrency
						) : (
							<Skeleton>
								<SkeletonItem width="10ch" />
							</Skeleton>
						)}
					</S.InfoCurrency>
					<S.InfoAltCurrencyContainer>
						{defaultCurrency ? (
							altCurrency?.balance && (
								<S.InfoAltCurrency>{`≈${altCurrency?.formattedBalance}`}</S.InfoAltCurrency>
							)
						) : (
							<Skeleton>
								<SkeletonItem width="10ch" />
							</Skeleton>
						)}
						{isBalanceInfoLocked && (
							<S.InfoLocked>
								<S.LockIconStyled color={colors.icons.main.enabled} height="16" width="16" />
								<span>{balanceInfoLocked}</span>
								<WalletTooltips type={WalletTooltipType.LOCKED} />
							</S.InfoLocked>
						)}
					</S.InfoAltCurrencyContainer>
				</S.InfoContainer>
			</S.ContainerWrapper>
		</S.Container>
	);
};
